import Button from "@/Components/Button/Button";
import Select from "@/Components/Select/Select";
import Spinner from "@/Components/Spinner/Spinner";

import Modal from "@/Components/Modal/Modal";
import TablesMap from "@/Components/Tables/TablesMap";
import { useModalMeta } from "@/shared/atoms/useModal";
import usePosOrderTable, {
  useSetPosOrderTable,
} from "@/shared/atoms/usePosOrderTable";
import { posTableDisplaysOptions } from "@/shared/atoms/usePosTableFilter";
import useCommon from "@/shared/hooks/useCommon";
import { useSessionId } from "@/shared/hooks/usePosOrderTableSessionHook";
import useChangeSessionTableMutation from "@/shared/mutations/orderSessions/useChangeSessionTableMutation";
import useActiveOrdersSessionsQuery from "@/shared/queries/orderssessions/useActiveOrdersSessionsQuery";
import useRestaurantMap from "@/shared/queries/restaurantMap/useRestaurantMap";
import useRestaurantMaps from "@/shared/queries/restaurantMap/useRestaurantMaps";
import useRestaurantMapsWithTables from "@/shared/queries/restaurantMap/useRestaurantWithTables";
import { asArray } from "@/shared/utils/helpers";
import { RestaurantTable } from "@/shared/utils/interfaces";
import { useEffect, useMemo, useState } from "react";
import useMeasure from "react-use-measure";
import PosTable from "./PosTable";
import usePosSettingsHook from "@/shared/hooks/usePosSettingsHook";

const Tables = ({ active = [], setTable }) => {
  const { settings } = usePosSettingsHook();
  const [showMap, setShowMap] = useState(settings.is_map_view ?? true);

  useEffect(() => {
    setShowMap(settings.is_map_view);
  }, [settings.is_map_view]);
  const [ref, bounds] = useMeasure();

  const { isPending: isSessionsPending, data: sessions } =
    useActiveOrdersSessionsQuery();
  const [currentTable] = usePosOrderTable();
  const t = useCommon();
  const [mapId, setMap] = useState(undefined);
  const { data: maps, isPending: isRestaurantsMapsPending } =
    useRestaurantMaps();
  const chosenMap = useMemo(
    () => asArray(maps)?.find((option) => option.item_id === mapId),
    [mapId, maps],
  );
  const { data: map, isPending } = useRestaurantMap(mapId, mapId !== undefined);
  useEffect(() => {
    if (mapId === undefined && maps?.length > 0) {
      setMap(maps[0].item_id);
    }
  }, [maps, mapId]);

  const filtered = useMemo(() => {
    return (
      map?.tables?.filter(
        (table) =>
          sessions?.find((session) =>
            asArray(session.current_table_ids)?.includes(table.item_id),
          ) ||
          table.item_id === currentTable.item_id ||
          sessions.every(
            (session) =>
              asArray(session.current_table_ids)?.includes(table.item_id) ===
              false,
          ),
      ) ?? []
    );
  }, [map, sessions]);

  return (
    <div>
      {isRestaurantsMapsPending || isSessionsPending ? (
        <Spinner />
      ) : (
        <div className="mb-8">
          <div className="flex flex-wrap gap-4">
            <div className="basis-[49%] flex-grow md:basis-auto">
              <Select
                menuPlacement="bottom"
                styles={{
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
                menuPortalTarget={document.getElementById("root")}
                options={maps ?? []}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.item_id}
                label={t("chooseUrMap")}
                value={chosenMap}
                onChange={(e) => {
                  setMap(e.item_id);
                }}
              />
            </div>
            <div className="basis-[49%] flex-grow md:basis-auto">
              <Select
                menuPlacement="bottom"
                styles={{
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
                menuPortalTarget={document.getElementById("root")}
                options={posTableDisplaysOptions}
                getOptionLabel={(option) => t(option.label)}
                getOptionValue={(option) => option.value}
                label={t("display")}
                value={posTableDisplaysOptions.find(
                  (option) => option.value === showMap,
                )}
                onChange={(e) => {
                  setShowMap(e.value);
                }}
              />
            </div>
          </div>
          {isPending ? (
            <Spinner />
          ) : (
            <div className="mt-8 flex flex-wrap gap-6">
              {filtered.length === 0 ? (
                <div>{t("noTablesAvailable")}</div>
              ) : showMap ? (
                <div ref={ref} className="h-[50rem] w-[67rem]">
                  <TablesMap
                    background_color={map?.background_color}
                    customSelectedTable={active}
                    onTableClick={(table) => {
                      setTable(table);
                    }}
                    width={bounds.width}
                    height={bounds.height}
                    tables={filtered}
                  />
                </div>
              ) : (
                filtered?.map((table) => (
                  <PosTable
                    forceNoSession={false}
                    active={active}
                    canChooseSession={true}
                    setTable={setTable}
                    table={table}
                  />
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PosChangeTablesModal = (props) => {
  const [{ current_table_ids }] = useModalMeta();
  const setGlobalTable = useSetPosOrderTable();
  const sessionId = useSessionId();
  const { refetch } = useActiveOrdersSessionsQuery();
  const mutate = useChangeSessionTableMutation();
  const [tables, setTables] = useState<RestaurantTable[]>([]);

  const { data, isPending } = useRestaurantMapsWithTables();

  useEffect(() => {
    if (data) {
      const initialTables = data
        .flatMap((map) => map.tables)
        .filter((table) => asArray(current_table_ids)?.includes(table.item_id));
      setTables(initialTables);
    }
  }, [data]);
  const tablesId = tables?.map((t) => t.item_id);
  const setTable = (table: RestaurantTable) => {
    if (tables.find((t) => t.item_id === table.item_id)) {
      setTables(tables.filter((t) => t.item_id !== table.item_id));
    } else {
      setTables([...tables, table]);
    }
  };

  const t = useCommon();
  const onSave = () => {
    if (tables.length === 0) return;
    mutate
      .mutateAsync({
        orderSessionId: sessionId,
        tables: tablesId,
      })
      .then(() => {
        props.onExit();
        refetch();
        setGlobalTable(tables[0]);
      });
  };
  return (
    <Modal onExit={props.onExit}>
      <div className="mb-4 text-[3rem] font-black">
        {t("changeTable")}
      </div>
      {isPending ? (
        <Spinner />
      ) : (
        <Tables active={tablesId} setTable={setTable} />
      )}

      {tables?.length !== 0 && (
        <Button isPending={mutate.isPending} onClick={onSave}>
          {t("save")}
        </Button>
      )}
    </Modal>
  );
};

export default PosChangeTablesModal;
